import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory } from 'react-router';
import { Autocomplete, Button, Card, ConfirmationModal, Page, Typography, useOverlayContext } from 'seagull';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { integrationModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { PracticeManagement, isPracticeManagementIntegrationItem } from '~/types/api.model';
import { getPMDetails } from '../pm.utils';
import PMMapServicesModal from '../PMMapServicesModal/PMMapServicesModal';
import styles from './PMSetupPage.module.scss';
export default function PMSetupPage(props) {
    const { pmName, testId, integration, getItems, doDisconnect, linkServiceTemplate, getServiceTemplateIntegratedIds } = props;
    const history = useHistory();
    const { fetchIntegrations, fetchKarbonIntegration, fetchKarbonUsers, api } = useStoreActions();
    const { showModal, showErrorToast, showSuccessToast, startLoader, stopLoader } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: texts } = useLocaleKeys('integrations.pmSetupPage');
    const karbonUsers = useStore(selectors.integration.karbonUsers);
    const renderCard = (text, action)=>/*#__PURE__*/ _jsx(Card, {
            autoHeight: true,
            padding: "m",
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.cardContent,
                children: [
                    text,
                    action
                ]
            })
        });
    useOnMount(()=>{
        if (pmName === PracticeManagement.Karbon) fetchKarbonUsers();
    });
    async function disconnect() {
        const confirmed = await showModal({
            content: (param)=>{
                let { dismissModal } = param;
                const cancel = ()=>dismissModal(false);
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    testId: "confirm-disconnect-modal",
                    skin: "danger",
                    title: texts.confirmDisconnectTitle({
                        pmName: getPMDetails(pmName).name
                    }),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.confirmDisconnectContent({
                            pmName: getPMDetails(pmName).name
                        })
                    }),
                    primaryButtonLabel: commonTexts.buttons.disconnect(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: cancel,
                    onCloseButtonClick: cancel
                });
            }
        });
        if (!confirmed) return;
        const disconnectLoader = 'disconnect';
        startLoader(disconnectLoader);
        const result = await handlePromise(doDisconnect());
        if (result.success) {
            await fetchIntegrations();
            goBack();
        } else {
            showErrorToast(commonTexts.toasts.failedToDisconnect(), 'failed-to-disconnect-toast');
        }
        stopLoader(disconnectLoader);
    }
    async function mapServices() {
        showModal({
            content: (p)=>/*#__PURE__*/ _jsx(PMMapServicesModal, _object_spread({
                    pmName: pmName,
                    getItems: getItems,
                    linkServiceTemplate: linkServiceTemplate,
                    getServiceTemplateIntegratedIds: getServiceTemplateIntegratedIds
                }, p))
        });
    }
    function goBack() {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push(routes.availableApps);
        }
    }
    return /*#__PURE__*/ _jsx(Page, {
        testId: testId,
        header: texts.integrationSetup({
            pmName: getPMDetails(pmName).name
        }),
        subheader: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                texts.setupSubheader({
                    pmName: getPMDetails(pmName).name
                }),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    openInNewTab: true,
                    href: getPMDetails(pmName).articleLink,
                    underline: "none",
                    children: "Learn more"
                })
            ]
        }),
        onClickBack: goBack,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.cards,
            children: [
                renderCard(/*#__PURE__*/ _jsx(Typography, {
                    children: texts.mapServicesDescription({
                        pmName: getPMDetails(pmName).name
                    })
                }), /*#__PURE__*/ _jsx(Button, {
                    testId: testId && "".concat(testId, "-map-services"),
                    className: styles.actionButton,
                    onClick: mapServices,
                    children: commonTexts.buttons.mapServices()
                })),
                pmName === PracticeManagement.Karbon && karbonUsers && integration && integrationModel.isLinked(integration) && renderCard(/*#__PURE__*/ _jsx(Typography, {
                    children: texts.setDefaultAssignee()
                }), /*#__PURE__*/ _jsx(Autocomplete, {
                    selectOnFocus: true,
                    disableClearable: true,
                    multiple: false,
                    size: "small",
                    testId: "default-work-assignee",
                    options: karbonUsers,
                    getOptionLabel: (option)=>option.name,
                    value: karbonUsers.find((user)=>user.id === integrationModel.karbonDefaultAssignee(integration)),
                    textFieldProps: {
                        minWidth: '240px'
                    },
                    onChange: async (e, value)=>{
                        if (!value || !isPracticeManagementIntegrationItem(value)) return;
                        const result = await handlePromise(api.karbon.updateKarbonIntegration(value.id));
                        if (result.success) {
                            await fetchKarbonIntegration();
                            showSuccessToast(commonTexts.toasts.defaultAssigneeUpdatedSuccessfully(), "karbon-assignee-success-toast");
                        } else {
                            showErrorToast(commonTexts.toasts.failedToUpdateDefaultAssignee(), "karbon-assignee-error-toast");
                        }
                    }
                })),
                renderCard(/*#__PURE__*/ _jsx(Typography, {
                    skin: "error",
                    children: texts.disconnectFrom({
                        pmName: getPMDetails(pmName).name
                    })
                }), /*#__PURE__*/ _jsx(Button, {
                    testId: testId && "".concat(testId, "-disconnect"),
                    className: styles.actionButton,
                    skin: "danger",
                    onClick: disconnect,
                    children: commonTexts.buttons.disconnect()
                }))
            ]
        })
    });
}
